/**
 * The commands over a level's saved edits:
 *
 * - `level.save` (Autosave, and Ctrl+S to save at once) writes the open
 *   level, as it is now, to this browser's saves (LevelSaves). Saving changes
 *   nothing in the level, so it isn't an undo step, as in Unreal. It logs at
 *   Verbose, since autosave runs it after every pause in editing.
 * - `level.reset { id }` (Reset to Default) drops a level's saved edits and, if
 *   it's open, puts it back as authored. It's undoable instead of asking for
 *   confirmation: its memo is the world as it was, and undo is last-in
 *   first-out, so every older undo step still finds the world it was recorded
 *   against.
 */
import { parsePayload } from "../../commands/payload";
import type { CommandDefinition } from "../../commands/types";
import type { ContentIndex, LevelManifest, ManifestEntity } from "../../assets/content";
import type { ComponentRegistry } from "../../world/schema/ComponentRegistry";
import { serializeWorld } from "../../world/serialization";
import type { WorldReader } from "../../world/World";
import type { LevelCommitter } from "../../world/WorldModule";
import type { Selection } from "../selection/Selection";
import type { LevelSave, LevelSaves } from "./LevelSaves";

export interface LevelSaveDeps {
  readonly world: WorldReader;
  readonly components: ComponentRegistry;
  readonly index: ContentIndex;
  readonly saves: LevelSaves;
  /** Swaps a level's world in, as a level load does (for a reset and its undo). */
  readonly levels: LevelCommitter;
  readonly selection: Selection;
  /** The open level's manifest as authored, without saved edits (LevelService). */
  readonly authored: () => LevelManifest | null;
}

/** What undoing a reset puts back: the open level's world, or a closed level's save. */
interface ResetMemo {
  readonly world: readonly ManifestEntity[] | null;
  readonly save: LevelSave | null;
}

export function levelSaveCommands(deps: LevelSaveDeps): CommandDefinition[] {
  return [saveCommand(deps), resetCommand(deps)] as CommandDefinition[];
}

function saveCommand(deps: LevelSaveDeps): CommandDefinition<Record<string, never>, undefined> {
  const entryOf = () => entryFor(deps, deps.world.getSnapshot().levelId);

  return {
    type: "level.save",
    undoable: false,
    quiet: true,
    validate: (raw) => {
      const shape = parsePayload(raw, {});
      if (!shape.ok) return shape;
      return entryOf() ? { ok: true, value: {} } : { ok: false, error: "No level is open" };
    },
    do: () => {
      const entry = entryOf();
      if (!entry) throw new Error("No level is open");
      // The hash ties the save to this version of the level's manifest.
      deps.saves.save(
        entry.id,
        entry.hash,
        serializeWorld(deps.world.getSnapshot(), deps.components),
      );
      return undefined;
    },
    undo: () => undefined,
    describe: () => `Save ${entryOf()?.name ?? "level"} in this browser`,
  };
}

function resetCommand(deps: LevelSaveDeps): CommandDefinition<{ id: string }, ResetMemo> {
  const isOpen = (id: string) => deps.world.getSnapshot().levelId === id;

  return {
    type: "level.reset",
    undoable: true,
    validate: (raw) => {
      const shape = parsePayload(raw, { id: "string" });
      if (!shape.ok) return shape;
      const { id } = shape.value;
      const entry = entryFor(deps, id);
      if (!entry) return { ok: false, error: `Level '${id}' not found` };
      // The open level may have edits not yet autosaved; a closed one only has its save.
      if (!isOpen(id) && !deps.saves.savedLevels.getSnapshot().has(id))
        return { ok: false, error: `${entry.name} has no edits to reset` };
      return { ok: true, value: { id } };
    },
    do: ({ id }) => {
      const authored = isOpen(id) ? deps.authored() : null;
      const memo: ResetMemo = {
        world: authored ? serializeWorld(deps.world.getSnapshot(), deps.components) : null,
        save: deps.saves.peek(id),
      };
      deps.saves.remove(id);
      if (authored) showLevel(deps, authored);
      return memo;
    },
    undo: ({ id }, memo) => {
      const authored = memo.world ? deps.authored() : null;
      if (authored && memo.world) {
        showLevel(deps, { ...authored, entities: memo.world });
        // The world as it was is what the level had: saved again, not just shown.
        const entry = entryFor(deps, id);
        if (entry) deps.saves.save(id, entry.hash, memo.world);
      } else if (memo.save) {
        deps.saves.save(id, memo.save.hash, memo.save.entities);
      }
    },
    describe: ({ id }) => `Reset ${entryFor(deps, id)?.name ?? id} to Default`,
  };
}

/** Swaps a version of the open level in, keeping the selection to entities that still exist. */
function showLevel(deps: LevelSaveDeps, manifest: LevelManifest): void {
  const { data } = deps.levels.prepare(manifest);
  deps.levels.commit(data);

  const { ids, active } = deps.selection.getSnapshot();
  const kept = ids.filter((id) => data.entities.has(id));
  const stillActive = active !== null && kept.includes(active) ? active : (kept.at(-1) ?? null);
  deps.selection.writer.set({ ids: kept, active: stillActive });
}

function entryFor(deps: LevelSaveDeps, levelId: string | null) {
  return deps.index.levels.find((level) => level.id === levelId) ?? null;
}
